import { type FieldValidationMetaInfo, localize } from '@vee-validate/i18n'
import en from '@vee-validate/i18n/dist/locale/en.json'
import sl from '@vee-validate/i18n/dist/locale/sl.json'
import {
    required,
    email,
    min,
    max,
    numeric,
    min_value,
    max_value,
    url,
    alpha_num,
    confirmed
} from '@vee-validate/rules'
import { IBAN } from 'ibankit'
import { defineRule, configure } from 'vee-validate'

import dayjs, { formatDate, isDateNull } from '@/libs/dayjs.ts'

defineRule('numeric', numeric)
defineRule('required', required)
defineRule('email', email)
defineRule('min', min)
defineRule('max', max)
defineRule('max', max)
defineRule('min_value', min_value)
defineRule('max_value', max_value)
defineRule('confirmed', confirmed)
defineRule('url', url)
defineRule('alpha_num', alpha_num)

defineRule('json', (value: string) => {
    try {
        if (value.length > 0) JSON.parse(value)
        return true
    } catch(error) {
        return false
    }
})

defineRule('alpha_num', (value: string) => {
    const re = /^[a-zA-Z0-9._/-]*$/
    return re.test(value)
})

defineRule('at_least_one_uppercase', (value: string) => {
    const re = /[A-Z]/g
    return re.test(value)
})

defineRule('at_least_one_digit', (value: string) => {
    const re = /\d/g
    return re.test(value)
})

defineRule('taxNumberSI', (val: string) => {
    if (!val) {
        return false
    }

    val = val.toUpperCase()
    if (val.startsWith('SI', 0)) {
        val = val.slice(2)
    }

    if (!val || val.length !== 8) return false

    for (const c of val) {
        if (isNaN(Number(c))) return false
    }

    let sum = 0
    for (let i = 0; i < 7; i++) {
        sum += Number(val[i]) * (8 - i)
    }
    let control = 11 - (sum % 11)
    if (control === 10 || control === 11) control = 0
    return val[7] === String(control)
})

defineRule('taxNumberHR', (val: string) => {
    if (!val) return false

    val = val.toUpperCase().trim()
    if (val.startsWith('HR')) {
        val = val.slice(2)
    }

    if (!val || val.length !== 11 || !/^\d+$/.test(val)) return false

    let a = 10
    for (let i = 0; i < 10; i++) {
        a = (Number(val[i]) + a) % 10
        if (a === 0) a = 10
        a = (a * 2) % 11
    }
    const control = (11 - a) % 10

    return Number(val[10]) === control
})

defineRule('iban', (val: string) => {
    // it has been empty
    if (!val || val.length === 0) return true

    return IBAN.isValid(val)
})

defineRule('min_or_equal_date', (val: string, params: string[]) => {
    // it has been empty
    if (!val || val.length === 0 || isDateNull(val) || params.length === 0) return true

    for (const item of params) {
        if (item.length === 0) continue
        const valid = dayjs(val).unix() > dayjs(item).unix() || dayjs(val).unix() === dayjs(item).unix()
        if (!valid) return false
    }

    return true
})

const getMinDate = (params: unknown[] | Record<string, unknown> | undefined) => {
    if (!params) return ''

    const dates = []
    const newParams = params as string[]
    for (const item of newParams) {
        dates.push(formatDate(item))
    }

    return dates.filter(Boolean).join(', ')
}

configure({
    generateMessage: localize({
        en: {
            messages: {
                ...en.messages,
                'at_least_one_uppercase': 'The {field} field must contain at least one uppercase letter',
                'at_least_one_digit': 'The {field} field must contain at least one digit',
                'min_or_equal_date': (data: FieldValidationMetaInfo) => `The ${data.field} field must be more than ${getMinDate(data.rule?.params)}`
            }
        },
        sl: {
            messages: {
                ...sl.messages,
                'at_least_one_uppercase': 'Polje {field} mora vsebovati vsaj eno veliko črko',
                'at_least_one_digit': 'Polje {field} mora vsebovati vsaj eno številko',
                'min_or_equal_date': (data: FieldValidationMetaInfo) => `Polje ${data.field} mora biti več od ${getMinDate(data.rule?.params)}`
            }
        }
    })
})
